import { EventListeners, MinzeAttrs, MinzeElement, Reactive } from 'minze'
import { config } from '../../../constants'

const DEFAULT_ASPECT_RATIO = 16 / 9
// Default Cloudflare image options
const DEFAULT_CF_WIDTH = 800
const DEFAULT_CF_HEIGHT = DEFAULT_CF_WIDTH / DEFAULT_ASPECT_RATIO
const DEFAULT_CF_FIT = 'cover'
const DEFAULT_CF_FORMAT = 'auto'

export interface LvcImage {
  src: string | null
  alt: string
  loading: 'lazy' | 'eager'
  error: boolean
  dataImageRemoveMask: boolean
  dataImagePlaceholder: string | null
  dataCfImageWidth: number
  dataCfImageHeight: number
  dataCfImageFit: string
  dataCfImageFormat: string
  dataCfImageAspectRatio: string | number
}
export class LvcImage extends MinzeElement {
  attrs: MinzeAttrs = [
    'src',
    'alt',
    ['loading', 'lazy'],
    ['data-image-remove-mask', false],
    'data-image-placeholder',
    'data-cf-image-width',
    'data-cf-image-height',
    'data-cf-image-aspect-ratio',
    ['data-cf-image-fit', DEFAULT_CF_FIT],
    ['data-cf-image-format', DEFAULT_CF_FORMAT],
  ]
  reactive: Reactive = [
    ['error', false],
    ['transformationFailed', false],
  ]

  handleError = () => {
    // If transformation failed and we're now showing the original image,
    // and that also fails, then show the placeholder
    if (this.transformationFailed) {
      this.error = true
    } else {
      // First error: transformation failed, try original image
      this.transformationFailed = true
    }
  }

  applyImageTransformations = (url: string) => {
    if (!url) return ''
    // Determine final width and height based on provided attributes
    // Maintain aspect ratio if only one dimension is provided
    // Fallback to default aspect ratio if none provided

    let calculatedWidth: number
    let calculatedHeight: number
    let aspectRatio: number = DEFAULT_ASPECT_RATIO

    // Parse aspect ratio safely
    if (this.dataCfImageAspectRatio) {
      if (typeof this.dataCfImageAspectRatio === 'string') {
        const parts = this.dataCfImageAspectRatio.split('/')
        if (parts.length === 2) {
          const num = parseFloat(parts[0])
          const den = parseFloat(parts[1])
          if (!isNaN(num) && !isNaN(den) && den !== 0) {
            aspectRatio = num / den
          } else {
            aspectRatio = DEFAULT_ASPECT_RATIO
          }
        } else {
          aspectRatio = DEFAULT_ASPECT_RATIO
        }
      } else if (typeof this.dataCfImageAspectRatio === 'number') {
        aspectRatio = this.dataCfImageAspectRatio
      }
    }

    // Logic to determine width and height
    if (this.dataCfImageWidth && this.dataCfImageHeight) {
      calculatedWidth = this.dataCfImageWidth
      calculatedHeight = this.dataCfImageHeight
    } else if (this.dataCfImageWidth) {
      calculatedWidth = this.dataCfImageWidth
      calculatedHeight = Math.round(this.dataCfImageWidth / aspectRatio)
    } else if (this.dataCfImageHeight) {
      calculatedHeight = this.dataCfImageHeight
      calculatedWidth = Math.round(this.dataCfImageHeight * aspectRatio)
    } else if (this.dataCfImageAspectRatio) {
      calculatedWidth = DEFAULT_CF_WIDTH
      calculatedHeight = Math.round(DEFAULT_CF_WIDTH / aspectRatio)
    } else {
      calculatedWidth = DEFAULT_CF_WIDTH
      calculatedHeight = DEFAULT_CF_HEIGHT
    }

    const options = {
      width: calculatedWidth,
      height: calculatedHeight,
      fit: this.dataCfImageFit,
      format: this.dataCfImageFormat,
    }

    let optionsString: string = ''
    try {
      optionsString = Object.entries(options)
        .map(([key, value]) => `${key}=${value}`)
        .join(',')
    } catch (error) {
      console.error('Error creating options string:', error)
      optionsString = `width=${DEFAULT_CF_WIDTH},height=${DEFAULT_CF_HEIGHT},fit=${DEFAULT_CF_FIT},format=${DEFAULT_CF_FORMAT}`
    }

    if (!optionsString) return url

    if (url.includes('lvc-medias.lavitrine.com') && !url.includes('/cdn-cgi/image/')) {
      try {
        const originalUrl = new URL(url)
        const fileName = originalUrl.pathname.split('/').pop()
        const supportedImageTypes = ['.jpg', '.jpeg', '.png', '.webp', '.avif', '.gif']
        const isValidImage =
          fileName &&
          fileName.trim() !== '' &&
          supportedImageTypes.some(type => fileName.toLowerCase().endsWith(type))
        if (isValidImage) {
          return `${originalUrl.origin}/cdn-cgi/image/${optionsString}/${fileName}`
        }
      } catch (error) {
        console.warn('Invalid URL for image transformation:', url, error)
      }
    }
    return url
  }

  renderImage = () => {
    let src: string

    if (this.error) {
      // Both transformation and original failed, show placeholder
      src = this.dataImagePlaceholder || ''
    } else if (this.transformationFailed) {
      // Transformation failed, show original untransformed image
      src = this.src || ''
    } else {
      // Normal case: show transformed image
      src = this.applyImageTransformations(this.src || '')
    }

    return `
      ${
        src
          ? `<img part="image" src="${src}" alt="${this.alt}" loading="${this.loading}"/>`
          : `<div part="image" class="img"></div>`
      }
    `
  }
  html = () => `
    ${this.renderImage()}
  `
  css = () => `
    .img, img {
      background-image:url('data:image/svg+xml,<svg width="433" height="267" viewBox="0 0 433 267" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(%23clip0_47_4)"><rect width="433" height="267" fill="%23D9F6FF" fill-opacity="0.5"/><path d="M190.924 128.892C190.42 130.005 190.282 130.425 190.09 131.569C189.899 132.713 190.013 133.889 190.42 134.973C190.826 136.057 191.511 137.01 192.403 137.733C193.295 138.455 194.361 138.921 195.49 139.082C196.643 139.236 197.817 139.068 198.883 138.594C199.95 138.121 200.869 137.36 201.543 136.394C202.214 135.382 202.602 134.204 202.667 132.985C202.733 131.766 202.472 130.552 201.913 129.472C201.367 128.158 200.349 127.105 199.068 126.527C197.619 125.96 196.985 125.873 195.49 126.159" stroke="%231E007D" stroke-opacity="0.2" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M221.307 136.127C222.043 135.241 222.073 134.747 222.264 133.602C222.455 132.458 222.342 131.283 221.935 130.199C221.528 129.115 220.843 128.162 219.951 127.439C219.059 126.716 217.994 126.25 216.865 126.09C215.711 125.935 214.538 126.104 213.471 126.577C212.405 127.051 211.485 127.812 210.811 128.778C210.141 129.79 209.752 130.968 209.687 132.187C209.622 133.406 209.883 134.62 210.442 135.7C210.988 137.014 212.005 138.067 213.287 138.644C214.736 139.212 215.798 139.195 217.293 138.908" stroke="%231E007D" stroke-opacity="0.3" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M239.447 127.278C238.561 126.542 238.067 126.512 236.923 126.32C235.779 126.129 234.603 126.243 233.519 126.65C232.435 127.057 231.482 127.742 230.759 128.633C230.037 129.525 229.571 130.591 229.41 131.72C229.256 132.873 229.424 134.047 229.898 135.113C230.371 136.18 231.132 137.1 232.098 137.773C233.11 138.444 234.288 138.832 235.507 138.898C236.726 138.963 237.94 138.702 239.02 138.143C240.334 137.597 241.387 136.58 241.965 135.298C242.532 133.849 242.515 132.787 242.228 131.291" stroke="%231E007D" stroke-opacity="0.4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_47_4"><rect width="433" height="267" fill="white"/></clipPath></defs></svg>');
      background-position: center;
      object-fit: cover;
      width: 100%;
      aspect-ratio: 16/9;
      ${
        !this.dataImageRemoveMask
          ? `
        -webkit-mask-image: url(${config.images.globalMask});
        mask-image: url(${config.images.globalMask});
        -webkit-mask-size: cover;
        mask-size: cover;
      `
          : ''
      } 
    }
    .img {
      background-image: none;
      background-color: #1E007D;
    }
  `
  eventListeners: EventListeners = [['img', 'error', this.handleError]]
}
